// src/app/services/http-client.base.ts
import {
  HttpClient, HttpHeaders, HttpParams, HttpResponse, HttpContext
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { TRACK_LOADING } from '../tokens/http-loading.token';


export interface HttpRequestOptions {
  params?: HttpParams;
  endpoint?: string; // Para segmentos de ruta adicionales (ej. /lotes/resumen)
  headers?: HttpHeaders;
  trackLoading?: boolean; // <--- NUEVO: opt-in para el loading global
}

@Injectable({ providedIn: 'root' })
export abstract class HttpClientBase {
  protected baseUrlApi: string;

  get apiBaseUrl() {
    return `${environment.backendHost}:${environment.backendPort}`;
  }

  constructor(protected http: HttpClient, protected baseUrl: string) {
    // evita doble slash si environment.base ya termina en /
    this.baseUrlApi = `${environment.base.replace(/\/+$/, '')}/${this.baseUrl.replace(/^\/+/, '')}`;
  }

  private buildUrl(endpoint?: string) {
    return endpoint
      ? `${this.baseUrlApi.replace(/\/+$/, '')}/${String(endpoint).replace(/^\/+/, '')}`
      : this.baseUrlApi;
  }

  private buildContext(track?: boolean) {
    let context = new HttpContext();
    if (track) context = context.set(TRACK_LOADING, true);
    return context;
  }

  public get<T>(options?: HttpRequestOptions): Observable<HttpResponse<T>> {
    const url = this.buildUrl(options?.endpoint);
    return this.http.get<T>(url, {
      params: options?.params,
      headers: options?.headers,
      observe: 'response',
      context: this.buildContext(options?.trackLoading),
    });
  }

  public show<T>(
    id: number | string,
    params?: HttpParams,
    headers?: HttpHeaders,
    trackLoading?: boolean
  ): Observable<T> {
    const url = `${this.baseUrlApi.replace(/\/+$/, '')}/${id}`;
    return this.http.get<T>(url, {
      params, headers,
      context: this.buildContext(trackLoading),
    });
  }

  public post<T>(
    body: any,
    endpoint?: string,
    headers?: HttpHeaders,
    trackLoading?: boolean
  ): Observable<T> {
    const url = this.buildUrl(endpoint);
    return this.http.post<T>(url, body, {
      headers,
      context: this.buildContext(trackLoading),
    });
  }

  protected put<T>(
    body: any,
    endpoint?: string,
    headers?: HttpHeaders,
    trackLoading?: boolean
  ): Observable<T> {
    const url = this.buildUrl(endpoint);

    return this.http.put<T>(url, body, {
      headers,
      context: this.buildContext(trackLoading),
    });
  }

  public delete<T>(
    endpoint: string,
    params?: HttpParams,
    headers?: HttpHeaders,
    trackLoading?: boolean
  ): Observable<T> {
    const url = this.buildUrl(endpoint);
    return this.http.delete<T>(url, {
      params, headers,
      context: this.buildContext(trackLoading),
    });
  }

  public downloadBlob(
    endpoint: string,
    params?: HttpParams,
    headers?: HttpHeaders,
    trackLoading?: boolean
  ): Observable<Blob> {
    const url = this.buildUrl(endpoint);
    return this.http.get(url, {
      params,
      headers,
      responseType: 'blob',
      context: this.buildContext(trackLoading),
    });
  }

  protected patch<T>(
    body: any,
    endpoint?: string,
    headers?: HttpHeaders,
    trackLoading?: boolean,
    params?: HttpParams, // <- opcional, útil para ?id=123
  ): Observable<T> {
    const url = this.buildUrl(endpoint);
    return this.http.patch<T>(url, body, {
      headers,
      params,
      context: this.buildContext(trackLoading),
    });
  }
}
